/**
 * Shared prefix-matching logic for a module's tab strip — factored out of
 * `ModuleLayout.tsx` so the shell-level breadcrumb (`AuthenticatedShell.tsx`)
 * can resolve the same "which tab is this route under" answer without
 * duplicating (and risking drift from) the tab strip's own matching rule.
 */
export interface TabLike {
  readonly id: string;
  /** Relative to the module base — `''` for the index/overview route. */
  readonly hrefSuffix: string;
  readonly label: string;
}

/**
 * Longest hrefSuffix wins, so a detail route (e.g. `/crm/quotes/QUO-1`)
 * still matches the `quotes` tab rather than falling through to the index
 * tab.
 */
export function computeActiveTab<T extends TabLike>(
  pathname: string,
  base: string,
  tabs: readonly T[],
): T {
  const sorted = [...tabs].sort((a, b) => b.hrefSuffix.length - a.hrefSuffix.length);
  for (const tab of sorted) {
    if (tab.hrefSuffix === '') {
      if (pathname === base || pathname === `${base}/`) return tab;
      continue;
    }
    const full = `${base}${tab.hrefSuffix}`;
    if (pathname === full || pathname.startsWith(`${full}/`)) return tab;
  }
  return tabs[0]!;
}
